import { CheckCircle2, Clock, Shield, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Course } from "@/types/course";

interface CourseCardProps {
	course: Course;
	animationIndex?: number;
}

const CourseFeatureItem = ({ feature }: { feature: string }) => (
	<li className="flex items-start gap-3 text-sm">
		<CheckCircle2 className="mb-2 text-primary h-5 w-5" />
		<span className="text-muted-foreground leading-snug">{feature}</span>
	</li>
);

const CourseInfoItem = ({
	icon: Icon,
	label,
	value,
}: {
	icon: typeof Clock;
	label: string;
	value: string;
}) => (
	<div className="flex items-center gap-2 text-sm">
		<Icon className="h-6 w-6 text-primary shrink-0" />
		<span className="text-muted-foreground">
			{label} <span className="font-medium text-foreground">{value}</span>
		</span>
	</div>
);

export const CourseCard = ({ course, animationIndex = 0 }: CourseCardProps) => {
	const handleCourseClick = () => {
		window.open(course.courseShortUrl, "_blank", "noopener,noreferrer");
	};

	return (
		<div
			className={`group rounded-3xl bg-card border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl overflow-hidden animate-slide-up stagger-${Math.min(animationIndex + 2, 6)} flex flex-col`}
		>
			{/* Course Image Header */}
			<div className="relative h-40 overflow-hidden bg-muted">
				<img
					src={course.courseImgWide}
					alt={`Curso: ${course.courseTitle} - Capacitação para mercado imobiliário`}
					className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
				/>
				<div className="absolute inset-0 bg-linear-to-t from-card via-card/50 to-transparent" />
				<div className="absolute top-4 right-4">
					<Badge className="bg-primary text-primary-foreground backdrop-blur-sm">
						<Star className="h-3 w-3 mr-1 fill-current" />
						Recomendado
					</Badge>
				</div>
			</div>

			{/* Avatar and Title Section */}
			<div className="relative px-6 -mt-16">
				<div className="flex items-end gap-4">
					<img
						src={course.courseImg}
						alt={`Thumbnail do curso ${course.courseTitle}`}
						className="w-32 h-32 rounded-3xl object-cover border-2 border-card shadow-lg shrink-0"
					/>
					<div className="flex-1 min-w-0 pb-2">
						<h3 className="text-2xl font-bold mb-1 leading-tight">
							{course.courseTitle}
						</h3>
						<p className="text-sm text-muted-foreground">
							{course.courseDetails.courseAuthorName}
						</p>
					</div>
				</div>
			</div>

			{/* Course Content */}
			<div className="px-6 pb-6 pt-4 flex flex-col flex-1">
				<p className="text-muted-foreground mb-6 line-clamp-3 text-sm leading-relaxed">
					{course.courseContent}
				</p>

				{/* Course Info */}
				<div className="space-y-3 mb-6 p-4 rounded-2xl bg-muted/30 border border-border/50">
					<CourseInfoItem
						icon={Clock}
						label="Acesso"
						value={course.courseDetails.coursePeriod}
					/>
					<CourseInfoItem
						icon={Shield}
						label="Garantia de"
						value={`${course.courseDetails.courseGuarantee} dias`}
					/>
				</div>

				{/* Features */}
				{course.courseFeatures && course.courseFeatures.length > 0 && (
					<div className="mb-6">
						<ul className="space-y-2">
							{course.courseFeatures.slice(0, 3).map((feature) => (
								<CourseFeatureItem key={feature} feature={feature} />
							))}
						</ul>
					</div>
				)}

				{/* Spacer to push button to bottom */}
				<div className="flex-1" />

				<Button
					className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-medium transition-all duration-200"
					onClick={handleCourseClick}
				>
					Saiba Mais
				</Button>
			</div>
		</div>
	);
};
